import {
  ACTIONFLOW_BROWSER_TRACE_CONTRACT_SCHEMA_VERSION,
  ACTIONFLOW_USER_OUTCOME_CONTRACT_SCHEMA_VERSION,
  ACTIONFLOW_USER_OUTCOME_PROOF_SCHEMA_VERSION,
  buildActionFlowBrowserTraceProof,
  buildActionFlowReadinessPacket,
  type ActionFlowBrowserTraceBinding,
  type ActionFlowBrowserTraceContract,
  type ActionFlowBrowserTraceObservation,
  type ActionFlowBrowserTraceProof,
  type ActionFlowReadinessPacket,
  type ActionFlowTopologySimulationProof,
  type ActionFlowTopologySimulationState,
  type ActionFlowUserOutcomeProof,
  type ContractValidationResult,
} from "@cogo/headless-core/actionflow-studio-readiness";
import type { LoadedApp } from "@/features/page-editor/store/types";
import type {
  StudioActionFlowReadinessEvidence,
  StudioActionFlowReadinessPacketEvidence,
} from "@/services/block-api-service";
import type {
  ActionFlowRendererProjection,
  ActionFlowStateDiff,
  ActionFlowTraceEntry,
} from "../types";

const MAX_BROWSER_BINDINGS = 3;

export function withActionFlowBrowserTraceReadiness(
  project: LoadedApp,
  trace: ActionFlowTraceEntry,
): LoadedApp {
  const readiness = readActionFlowReadiness(project);
  if (!readiness || !readiness.required || trace.source === "dry-run")
    return project;

  const proofInput = buildBrowserTraceProofInput(readiness, trace);
  if (!proofInput) return project;

  const browserTraceProof = buildActionFlowBrowserTraceProof(
    proofInput.contract,
    proofInput.userOutcomeProof,
    proofInput.observations,
  );
  const readinessPacket = buildActionFlowReadinessPacket({
    validationResult: rebuildValidationResult(readiness),
    simulationProof: rebuildSimulationProof(readiness),
    userOutcomeProof: proofInput.userOutcomeProof,
    browserTraceProof,
  });
  const nextReadiness = mergeBrowserTraceReadiness(
    readiness,
    browserTraceProof,
    readinessPacket,
  );

  return attachActionFlowReadiness(project, nextReadiness);
}

function buildBrowserTraceProofInput(
  readiness: StudioActionFlowReadinessEvidence,
  trace: ActionFlowTraceEntry,
): {
  contract: ActionFlowBrowserTraceContract;
  observations: ActionFlowBrowserTraceObservation[];
  userOutcomeProof: ActionFlowUserOutcomeProof;
} | null {
  if (trace.status !== "pass") return null;

  const pairs = deriveBrowserTracePairs(trace);
  if (pairs.length === 0) return null;

  const contract: ActionFlowBrowserTraceContract = {
    schemaVersion: ACTIONFLOW_BROWSER_TRACE_CONTRACT_SCHEMA_VERSION,
    scenarioId: `${readiness.userOutcomeProof?.scenarioId ?? "studio-actionflow"}:browser`,
    userGoal: `Verify ${trace.flowName} in the Studio preview browser.`,
    userValue:
      "The generated ActionFlow is reflected in the rendered application, not only in headless state proof.",
    triggerLabel: trace.eventLabel,
    evidenceRefs: [
      "studio.preview.actionFlowTrace",
      "studio.preview.rendererProjection",
      ...pairs.flatMap((pair) => pair.binding.evidenceRefs),
    ],
    bindings: pairs.map((pair) => pair.binding),
  };

  return {
    contract,
    observations: pairs.map((pair) => pair.observation),
    userOutcomeProof: rebuildUserOutcomeProof(readiness, trace),
  };
}

function deriveBrowserTracePairs(trace: ActionFlowTraceEntry): Array<{
  binding: ActionFlowBrowserTraceBinding;
  observation: ActionFlowBrowserTraceObservation;
}> {
  const beforeByElementId = new Map(
    (trace.rendererProjectionBefore ?? []).map((item) => [
      item.elementId,
      item.text,
    ]),
  );
  const usedElements = new Set<string>();
  const pairs: Array<{
    binding: ActionFlowBrowserTraceBinding;
    observation: ActionFlowBrowserTraceObservation;
  }> = [];

  for (const diff of trace.stateDiffs) {
    const projection = findProjectionForDiff(
      diff,
      trace.rendererProjection,
      beforeByElementId,
      usedElements,
    );
    if (!projection) continue;
    usedElements.add(projection.elementId);

    const selector = `[data-cogo-element-id="${escapeAttribute(projection.elementId)}"]`;
    const bindingId = stableBindingId(diff.path, projection.elementId);
    const beforeText = beforeByElementId.get(projection.elementId) ?? null;
    const afterText = projection.text;
    pairs.push({
      binding: {
        id: bindingId,
        selector,
        statePath: diff.path,
        expectedText: afterText,
        evidenceRefs: [
          `actionFlowTrace.${trace.traceId}`,
          `stateDiff.${diff.path}`,
          `rendererProjection.${projection.elementId}`,
        ],
      },
      observation: {
        bindingId,
        selector,
        beforeText,
        afterText,
        selectorFound: true,
        changed: beforeText !== afterText,
      },
    });

    if (pairs.length >= MAX_BROWSER_BINDINGS) break;
  }

  return pairs;
}

function findProjectionForDiff(
  diff: ActionFlowStateDiff,
  projections: ActionFlowRendererProjection[],
  beforeByElementId: ReadonlyMap<string, string>,
  usedElements: ReadonlySet<string>,
): ActionFlowRendererProjection | null {
  const needles = valueNeedles(diff.after);
  if (needles.length === 0) return null;
  const candidates = projections.filter((projection) => {
    if (usedElements.has(projection.elementId)) return false;
    const text = normalizeText(projection.text);
    return needles.some((needle) => text.includes(needle));
  });
  if (candidates.length === 0) return null;

  return (
    candidates.find(
      (projection) =>
        beforeByElementId.get(projection.elementId) !== projection.text,
    ) ??
    candidates[0] ??
    null
  );
}

function valueNeedles(value: unknown): string[] {
  const raw = primitiveNeedles(value);
  return Array.from(
    new Set(raw.map(normalizeText).filter((item) => item.length > 0)),
  );
}

function primitiveNeedles(value: unknown): string[] {
  if (typeof value === "string") return [value];
  if (typeof value === "number")
    return [String(value), new Intl.NumberFormat("en-US").format(value)];
  if (typeof value === "boolean") return [String(value)];
  if (Array.isArray(value)) return [String(value.length)];
  if (!value || typeof value !== "object") return [];

  const record = value as Record<string, unknown>;
  const priorityKeys = [
    "name",
    "title",
    "label",
    "status",
    "count",
    "total",
    "amount",
    "price",
  ];
  const priority = priorityKeys.flatMap((key) => primitiveNeedles(record[key]));
  const shallow = Object.values(record)
    .flatMap((item) =>
      typeof item === "string" ||
      typeof item === "number" ||
      typeof item === "boolean"
        ? [String(item)]
        : [],
    )
    .slice(0, 8);
  return [...priority, ...shallow];
}

function normalizeText(value: string): string {
  return value.toLowerCase().replace(/\s+/g, " ").trim();
}

function escapeAttribute(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function stableBindingId(path: string, elementId: string): string {
  const raw = `${path}:${elementId}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return raw || "browser-trace-binding";
}

function rebuildValidationResult(
  readiness: StudioActionFlowReadinessEvidence,
): ContractValidationResult {
  return {
    ok: readiness.headlessValidation?.ok ?? readiness.ontologyValidation.ok,
    errors:
      readiness.headlessValidation?.errors ??
      readiness.ontologyValidation.errors,
  };
}

function rebuildSimulationProof(
  readiness: StudioActionFlowReadinessEvidence,
): ActionFlowTopologySimulationProof | undefined {
  const simulation = readiness.headlessSimulation;
  if (!simulation) return undefined;
  const emptyState = emptySimulationState();
  return {
    schemaVersion: "actionflow-topology-simulation-proof.v1",
    source: "studio-preview-browser-trace-readiness",
    generatedAt: readiness.generatedAt,
    ok: simulation.ok,
    validationErrors: simulation.validationErrors,
    errors: simulation.errors,
    initialState: emptyState,
    finalState: emptyState,
    steps: [],
    summary: {
      primitivesSimulated: simulation.summary.primitivesSimulated ?? 0,
      stateWrites: simulation.summary.stateWrites ?? 0,
      resultWrites: simulation.summary.resultWrites ?? 0,
      sideEffectMocks: simulation.summary.sideEffectMocks ?? 0,
      blockedPrimitives: simulation.summary.blockedPrimitives ?? 0,
      skippedPrimitives: simulation.summary.skippedPrimitives ?? 0,
    },
  };
}

function rebuildUserOutcomeProof(
  readiness: StudioActionFlowReadinessEvidence,
  trace: ActionFlowTraceEntry,
): ActionFlowUserOutcomeProof {
  const proof = readiness.userOutcomeProof;
  return {
    schemaVersion: ACTIONFLOW_USER_OUTCOME_PROOF_SCHEMA_VERSION,
    contractVersion: ACTIONFLOW_USER_OUTCOME_CONTRACT_SCHEMA_VERSION,
    scenarioId: proof?.scenarioId ?? `${trace.flowName}:user-outcome`,
    userGoal: `Execute ${trace.flowName} from Studio preview.`,
    userValue:
      "The user-visible generated application behavior is backed by runtime and browser proof.",
    evaluatedAt: readiness.generatedAt,
    ok: proof?.ok ?? false,
    readiness: proof?.readiness ?? "blocked",
    outcomes: [],
    blockingFailures: proof?.blockingFailures ?? [
      "User outcome proof is missing.",
    ],
    simulationSummary: {
      primitivesSimulated:
        readiness.headlessSimulation?.summary.primitivesSimulated ?? 0,
      stateWrites: readiness.headlessSimulation?.summary.stateWrites ?? 0,
      resultWrites: readiness.headlessSimulation?.summary.resultWrites ?? 0,
      sideEffectMocks:
        readiness.headlessSimulation?.summary.sideEffectMocks ?? 0,
      blockedPrimitives:
        readiness.headlessSimulation?.summary.blockedPrimitives ?? 0,
      skippedPrimitives:
        readiness.headlessSimulation?.summary.skippedPrimitives ?? 0,
    },
    evidenceRefs: proof?.evidenceRefs ?? [],
  };
}

function emptySimulationState(): ActionFlowTopologySimulationState {
  return {
    local: {},
    appData: {},
    computed: {},
    _results: {},
    env: {},
    route: {},
    props: {},
    event: {},
    datasource: {},
  };
}

function mergeBrowserTraceReadiness(
  readiness: StudioActionFlowReadinessEvidence,
  browserTraceProof: ActionFlowBrowserTraceProof,
  readinessPacket: ActionFlowReadinessPacket,
): StudioActionFlowReadinessEvidence {
  const browserTraceValid = browserTraceProof.ok;
  const reasons = browserTraceValid
    ? readiness.reasons.filter(
        (reason) => !reason.includes("browser-trace proof is missing"),
      )
    : Array.from(
        new Set([...readiness.reasons, ...browserTraceProof.blockingFailures]),
      );

  return {
    ...readiness,
    status: browserTraceValid ? readiness.status : "invalid",
    generatedAt: new Date().toISOString(),
    evidenceRefs: Array.from(
      new Set([
        ...readiness.evidenceRefs,
        "@cogo/headless-core.buildActionFlowBrowserTraceProof",
        "studio.preview.actionFlowTrace",
        "studio.preview.rendererProjection",
      ]),
    ),
    reasons,
    browserTraceProof: {
      schemaVersion: browserTraceProof.schemaVersion,
      contractVersion: browserTraceProof.contractVersion,
      scenarioId: browserTraceProof.scenarioId,
      ok: browserTraceProof.ok,
      readiness: browserTraceProof.readiness,
      triggerLabel: browserTraceProof.triggerLabel,
      evaluationCount: browserTraceProof.evaluations.length,
      blockingFailures: browserTraceProof.blockingFailures,
      evidenceRefs: browserTraceProof.evidenceRefs,
    },
    readinessPacket: summarizeReadinessPacket(readinessPacket),
    summary: {
      ...readiness.summary,
      browserTraceValid,
      headlessPublishAllowed: readinessPacket.publishAllowed,
    },
  };
}

function summarizeReadinessPacket(
  packet: ActionFlowReadinessPacket,
): StudioActionFlowReadinessPacketEvidence {
  return {
    schemaVersion: packet.schemaVersion,
    status: packet.status,
    publishAllowed: packet.publishAllowed,
    stages: packet.stages,
    blockingFailures: packet.blockingFailures,
    evidenceRefs: packet.evidenceRefs,
    policy: { ...packet.policy },
  };
}

function readActionFlowReadiness(
  project: LoadedApp,
): StudioActionFlowReadinessEvidence | null {
  const projectRecord = project as unknown as Record<string, unknown>;
  const manifest = project.manifest as unknown as Record<string, unknown>;
  const metadata = projectRecord.metadata as
    | Record<string, unknown>
    | undefined;
  const candidate =
    metadata?.actionFlowReadiness ?? manifest.actionFlowReadiness;
  if (
    candidate &&
    typeof candidate === "object" &&
    (candidate as Record<string, unknown>).schemaVersion ===
      "studio-actionflow-readiness.v1"
  ) {
    return candidate as StudioActionFlowReadinessEvidence;
  }
  return null;
}

function attachActionFlowReadiness(
  project: LoadedApp,
  readiness: StudioActionFlowReadinessEvidence,
): LoadedApp {
  const projectRecord = project as unknown as Record<string, unknown>;
  const metadata = {
    ...((projectRecord.metadata as Record<string, unknown> | undefined) ?? {}),
    actionFlowReadiness: readiness,
  };
  const manifest = {
    ...(project.manifest as unknown as Record<string, unknown>),
    actionFlowReadiness: readiness,
  };
  return {
    ...project,
    metadata,
    manifest: manifest as unknown as LoadedApp["manifest"],
  } as LoadedApp;
}
